'use client';

import { faSearch } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Select, InputField } from '@pickleballinc/react-ui';
import { useRouter, useSearchParams } from 'next/navigation';
import { useState } from 'react';

import { Button } from '@/components/atoms/Button';
import { Options } from '@/utils/types';

export interface ITeamsPageFilters {
	search: string;
	season: string;
	division: string;
	district: string;
	waiting_list: string;
}

interface SpectatorPageFiltersForTeamsPageProps {
	seasons: Options[];
	divisions: Options[];
	districts: Options[];
	activeSeason?: string;
}

const getNewSearchParams = (filters: ITeamsPageFilters) => {
	const newSearchParams = new URLSearchParams(window.location.search);

	if (filters.season) {
		newSearchParams.set('season', filters.season);
	} else {
		newSearchParams.delete('season');
	}

	if (filters.waiting_list) {
		newSearchParams.set('waiting_list', filters.waiting_list);
	} else {
		newSearchParams.delete('waiting_list');
	}

	if (filters.search) {
		newSearchParams.set('search', filters.search);
	} else {
		newSearchParams.delete('search');
	}

	if (filters.district) {
		newSearchParams.set('district', filters.district);
	} else {
		newSearchParams.delete('district');
	}

	if (filters.division) {
		newSearchParams.set('division', filters.division);
	} else {
		newSearchParams.delete('division');
	}

	return newSearchParams || '';
};

const waitingListOptions: Options[] = [
	{ value: '', label: 'All Teams' },
	{ value: 'true', label: 'Waiting list only' }
];

export const SpectatorPageFiltersForTeamsPage = ({ seasons, divisions, districts, activeSeason }: SpectatorPageFiltersForTeamsPageProps) => {
	const router = useRouter();
	const searchParams = useSearchParams();

	const [filters, setFilters] = useState<ITeamsPageFilters>({
		search: searchParams?.get('search') ?? '',
		season: searchParams?.get('season') ?? activeSeason ?? seasons[0]?.value ?? '',
		division: searchParams?.get('division') ?? '',
		district: searchParams?.get('district') ?? '',
		waiting_list: searchParams?.get('waiting_list') ?? ''
	});

	const divisionsWithAllOption: Options[] = divisions.length >= 1 ? [{ value: '', label: 'All divisions' }, ...divisions] : [];
	const districtsWithAllOption: Options[] = districts.length >= 1 ? [{ value: '', label: 'All districts' }, ...districts] : [];

	const emptyFilters: ITeamsPageFilters = {
		search: '',
		season: activeSeason ?? seasons[0]?.value ?? '',
		division: '',
		district: '',
		waiting_list: ''
	};

	const handleReset = () => {
		setFilters(emptyFilters);
		router.replace('?');
	};

	return (
		<div className="flex flex-col md:grid md:grid-cols-2 gap-3 md:gap-x-6 md:gap-y-4 mt-4 lg:mt-6 lg:mb-2">
			<div>
				<Select
					options={seasons}
					selectTheme="orange"
					label="Select season"
					id="seasons-select"
					className="w-full border !rounded-md !border-gray-300 mt-1.5"
					labelClassName="text-sm font-medium leading-5 text-gray-700"
					value={seasons.find(({ value }) => value === filters.season) || seasons[0]}
					onChange={(option) => {
						setFilters({ ...filters, season: (option as Options).value });
					}}
				/>
			</div>

			<div>
				<Select
					options={waitingListOptions}
					selectTheme="orange"
					label="Waiting List"
					id="waiting-lists-select"
					className="w-full border !rounded-md !border-gray-300 mt-1.5"
					labelClassName="text-sm font-medium leading-5 text-gray-700"
					value={waitingListOptions.find(({ value }) => value === filters.waiting_list) || waitingListOptions[0]}
					onChange={(option) => {
						setFilters({ ...filters, waiting_list: (option as Options).value });
					}}
				/>
			</div>

			<div className="relative w-full">
				<InputField
					focusColor="orange"
					type="text"
					placeholder="Search team"
					value={filters.search}
					onChange={(e) => setFilters({ ...filters, search: e.target.value })}
					onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
						if (event.key === 'Enter') {
							router.replace(`?${getNewSearchParams(filters)?.toString()}`);
							event.currentTarget.blur();
						}
					}}
					className="border-1 w-full !rounded-md !border-gray-300 pl-6"
					maxLength={100}
					data-testid="search-field"
				/>
				<FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
			</div>
			<div className="hidden md:block" />

			<div className="flex items-start xl:items-center">
				<Select
					options={districtsWithAllOption}
					selectTheme="orange"
					id="district-select"
					className={`w-full border !rounded-md !border-gray-300 ${districts.length === 1 && 'hidden'}`}
					value={districtsWithAllOption.find(({ value }) => value === filters.district) || districtsWithAllOption[0]}
					onChange={(option) => {
						setFilters({ ...filters, district: (option as Options).value });
					}}
				/>
			</div>

			<div className="flex flex-col xl:flex-row gap-3 items-start md:items-end xl:items-center w-full justify-end">
				<Select
					options={divisionsWithAllOption}
					selectTheme="orange"
					id="division-select"
					className={`w-full border !rounded-md !border-gray-300 ${divisions.length === 1 && 'hidden'}`}
					value={divisionsWithAllOption.find(({ value }) => value === filters.division) || divisionsWithAllOption[0]}
					onChange={(option) => {
						setFilters({ ...filters, division: (option as Options).value });
					}}
				/>
				<div className="flex flex-row gap-4">
					<Button
						onClick={() => router.replace(`?${getNewSearchParams(filters)?.toString()}`)}
						className="h-11 bg-orange-500 font-semibold text-white hover:bg-orange-700"
					>
						Apply
					</Button>

					<Button
						onClick={handleReset}
						className="bg-white hover:bg-gray-50 text-base font-semibold leading-6 text-gray-700 rounded-lg border border-gray-300 px-[18px] py-2.5 !h-11"
					>
						Reset
					</Button>
				</div>
			</div>
		</div>
	);
};
